import React, { Component } from 'react';

class SlideCanvas extends Component {

    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.editableRef = React.createRef();
        this.state = {
            scale: 1,
            editingElementIndex: null,
            editingOriginalText: null,
        };
    }

    componentDidMount() {
        this.updateScale();
        this.resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(() => this.updateScale());
        });
        if (this.containerRef.current) {
            this.resizeObserver.observe(this.containerRef.current);
        }
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    updateScale = () => {
        if (this.containerRef.current) {
            const containerWidth = this.containerRef.current.offsetWidth - 32;
            const scale = Math.min(containerWidth / 1920, 1);
            this.setState({ scale });
        }
    }

    deepClone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    getElementPath(cue, flatIndex) {
        if (!cue) return null;
        let elementCounter = 0;
        const actions = cue.actions || [];
        for (let ai = 0; ai < actions.length; ai++) {
            const baseSlide = actions[ai].slide && actions[ai].slide.presentation && actions[ai].slide.presentation.baseSlide;
            if (!baseSlide || !baseSlide.elements) continue;
            for (let ei = 0; ei < baseSlide.elements.length; ei++) {
                if (elementCounter === flatIndex) {
                    return { actionIndex: ai, elementInActionIndex: ei };
                }
                elementCounter++;
            }
        }
        return null;
    }

    replaceTextInRtf(rtfData, newText) {
        if (!rtfData) return rtfData;
        try {
            const decoded = atob(rtfData);
            const pardIdx = decoded.lastIndexOf('\\pard');
            if (pardIdx === -1) return rtfData;

            const afterPard = decoded.substring(pardIdx);
            const braceIdx = afterPard.indexOf('{');
            if (braceIdx === -1) return rtfData;

            const contentStart = pardIdx + braceIdx;
            // Find the matching closing brace for this content block
            let depth = 1;
            let contentEnd = contentStart + 1;
            for (let i = contentStart + 1; i < decoded.length; i++) {
                const ch = decoded[i];
                if (ch === '{') {
                    depth++;
                } else if (ch === '}') {
                    depth--;
                    if (depth === 0) {
                        contentEnd = i;
                        break;
                    }
                }
            }

            // Rebuild the content block: extract RTF control sequences, strip old text, insert new text
            const contentBlock = decoded.substring(contentStart + 1, contentEnd);
            // Walk through to separate control sequences from text
            let controlPrefix = '';
            let i = 0;
            while (i < contentBlock.length) {
                const ch = contentBlock[i];
                if (ch === '\\') {
                    // RTF control sequence
                    let j = i + 1;
                    while (j < contentBlock.length && contentBlock[j] !== ' ' && contentBlock[j] !== '\\' && contentBlock[j] !== '{' && contentBlock[j] !== '}' && contentBlock[j] !== '\n' && contentBlock[j] !== '\r') {
                        j++;
                    }
                    if (j < contentBlock.length && contentBlock[j] === ' ') j++;
                    controlPrefix += contentBlock.substring(i, j);
                    i = j;
                } else if (ch === '\n' || ch === '\r') {
                    controlPrefix += ch;
                    i++;
                } else {
                    // Start of actual text — skip all remaining text characters
                    break;
                }
            }

            // Escape non-ASCII characters for RTF (\ansi mode)
            var safeText = '';
            for (var ci = 0; ci < newText.length; ci++) {
                var code = newText.charCodeAt(ci);
                if (code > 127) {
                    safeText += "\\'" + ('0' + code.toString(16)).slice(-2);
                } else if (newText[ci] === '\\' || newText[ci] === '{' || newText[ci] === '}') {
                    safeText += '\\' + newText[ci];
                } else {
                    safeText += newText[ci];
                }
            }
            const newContent = '{' + controlPrefix + safeText + '}';
            const result = decoded.substring(0, contentStart) + newContent + decoded.substring(contentEnd + 1);
            return btoa(result);
        } catch (e) {
            return rtfData;
        }
    }

    enterEditMode = (index, element) => {
        const textProps = this.getTextProps(element);
        if (!textProps) return;
        this.setState({
            editingElementIndex: index,
            editingOriginalText: textProps.content,
        }, () => {
            if (this.editableRef.current) {
                this.editableRef.current.focus();
                // Select all text
                const range = document.createRange();
                range.selectNodeContents(this.editableRef.current);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
        });
    }

    exitEditMode = (save) => {
        if (this.state.editingElementIndex === null) return;

        if (save && this.editableRef.current && this.props.onPresentationChange) {
            const newText = this.editableRef.current.textContent;
            if (newText !== this.state.editingOriginalText) {
                const { cue, presentation, slideIndex } = this.props;
                const elements = this.getAllElements(cue);
                const element = elements[this.state.editingElementIndex];
                if (element && element.element && element.element.text) {
                    const currentRtf = element.element.text.rtfData;
                    const newRtf = this.replaceTextInRtf(currentRtf, newText);
                    const path = this.getElementPath(cue, this.state.editingElementIndex);
                    if (path) {
                        const updated = this.deepClone(presentation);
                        updated.cues[slideIndex].actions[path.actionIndex].slide.presentation.baseSlide.elements[path.elementInActionIndex].element.text.rtfData = newRtf;
                        this.props.onPresentationChange(updated);
                    }
                }
            }
        }

        this.setState({ editingElementIndex: null, editingOriginalText: null });
    }

    handleEditKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            this.exitEditMode(false);
        } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.exitEditMode(true);
        }
    }

    getAllElements(cue) {
        if (!cue) return [];
        const actions = cue.actions || [];
        const allElements = [];
        actions.forEach(action => {
            const baseSlide = action.slide && action.slide.presentation && action.slide.presentation.baseSlide;
            if (!baseSlide || !baseSlide.elements) return;
            baseSlide.elements.forEach(el => allElements.push(el));
        });
        return allElements;
    }

    getElementStyle(element) {
        const el = element.element;
        if (!el) return {};

        const bounds = el.bounds || {};
        const origin = bounds.origin || {};
        const size = bounds.size || {};

        const style = {
            position: 'absolute',
            left: (origin.x || 0) + 'px',
            top: (origin.y || 0) + 'px',
            width: (size.width || 200) + 'px',
            height: (size.height || 100) + 'px',
        };

        if (el.opacity !== undefined) {
            style.opacity = el.opacity;
        }

        if (el.rotation !== undefined && el.rotation !== 0) {
            style.transform = 'rotate(' + el.rotation + 'deg)';
        }

        return style;
    }

    rgbaFromProtoColor(c, fallbackAlpha) {
        if (!c) return null;
        // Empty color object (no fields set) means transparent
        if (!c.red && !c.green && !c.blue && c.alpha === undefined) return null;
        const r = Math.round((c.red || 0) * 255);
        const g = Math.round((c.green || 0) * 255);
        const b = Math.round((c.blue || 0) * 255);
        const a = c.alpha !== undefined ? c.alpha : (fallbackAlpha !== undefined ? fallbackAlpha : 1);
        return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
    }

    // Parse base64-encoded RTF into an array of styled text runs.
    // Each run: { text, bold, italic, underline }
    // Handles hex escapes (\'xx), unicode escapes (\uN), \uc replacement
    // char counts, and inline formatting (\b, \i, \ul).
    parseRtfRuns(rtfData, defaults) {
        if (!rtfData) return [];
        try {
            const decoded = atob(rtfData);
            const pardIdx = decoded.lastIndexOf('\\pard');
            if (pardIdx === -1) return [];

            var defs = defaults || {};
            let pos = pardIdx + 5; // skip past \pard
            const runs = [];
            let currentText = '';
            let bold = defs.bold || false, italic = defs.italic || false, underline = false, ucCount = 1;
            const stateStack = [];

            function flushRun() {
                if (currentText.length > 0) {
                    runs.push({ text: currentText, bold: bold, italic: italic, underline: underline });
                    currentText = '';
                }
            }

            while (pos < decoded.length) {
                var ch = decoded[pos];

                if (ch === '{') {
                    stateStack.push({ bold: bold, italic: italic, underline: underline, ucCount: ucCount });
                    pos++;
                } else if (ch === '}') {
                    flushRun();
                    if (stateStack.length > 0) {
                        var prev = stateStack.pop();
                        bold = prev.bold;
                        italic = prev.italic;
                        underline = prev.underline;
                        ucCount = prev.ucCount;
                    } else {
                        break;
                    }
                    pos++;
                } else if (ch === '\\') {
                    pos++;
                    if (pos >= decoded.length) break;
                    var next = decoded[pos];

                    if (next === "'") {
                        // Hex escape \'xx
                        if (pos + 2 < decoded.length) {
                            var hex = decoded.substring(pos + 1, pos + 3);
                            var charCode = parseInt(hex, 16);
                            if (!isNaN(charCode)) currentText += String.fromCharCode(charCode);
                            pos += 3;
                        } else { pos++; }
                    } else if (next === '\\') {
                        currentText += '\\'; pos++;
                    } else if (next === '{') {
                        currentText += '{'; pos++;
                    } else if (next === '}') {
                        currentText += '}'; pos++;
                    } else if (next === '~') {
                        currentText += '\u00A0'; pos++;
                    } else if (next === '_') {
                        currentText += '\u2011'; pos++;
                    } else if (next === '-') {
                        pos++; // optional hyphen
                    } else if (next === '\n' || next === '\r') {
                        pos++; // par break via escaped newline
                    } else {
                        // Parse control word name
                        var word = '';
                        while (pos < decoded.length && ((decoded[pos] >= 'a' && decoded[pos] <= 'z') || (decoded[pos] >= 'A' && decoded[pos] <= 'Z'))) {
                            word += decoded[pos]; pos++;
                        }
                        // Parse optional numeric parameter
                        var paramStr = '';
                        if (pos < decoded.length && (decoded[pos] === '-' || (decoded[pos] >= '0' && decoded[pos] <= '9'))) {
                            if (decoded[pos] === '-') { paramStr += '-'; pos++; }
                            while (pos < decoded.length && decoded[pos] >= '0' && decoded[pos] <= '9') {
                                paramStr += decoded[pos]; pos++;
                            }
                        }
                        // Skip space delimiter
                        if (pos < decoded.length && decoded[pos] === ' ') pos++;

                        var param = paramStr.length > 0 ? parseInt(paramStr, 10) : null;

                        if (word === 'b') {
                            flushRun();
                            bold = param !== 0;
                        } else if (word === 'i') {
                            flushRun();
                            italic = param !== 0;
                        } else if (word === 'ul') {
                            flushRun();
                            underline = param !== 0;
                        } else if (word === 'ulnone') {
                            flushRun();
                            underline = false;
                        } else if (word === 'uc') {
                            ucCount = param !== null ? param : 1;
                        } else if (word === 'u' && param !== null) {
                            // Unicode escape
                            var code = param;
                            if (code < 0) code += 65536;
                            if (code === 8232 || code === 8233) {
                                currentText += '\n';
                            } else {
                                currentText += String.fromCharCode(code);
                            }
                            // Skip replacement characters per \uc count
                            for (var sk = 0; sk < ucCount; sk++) {
                                if (pos >= decoded.length) break;
                                if (decoded[pos] === '\\' && pos + 1 < decoded.length && decoded[pos + 1] === "'") {
                                    pos += 4; // skip \'xx
                                } else if (decoded[pos] !== '\\' && decoded[pos] !== '{' && decoded[pos] !== '}') {
                                    pos++; // skip literal replacement char
                                } else {
                                    break;
                                }
                            }
                        }
                        // All other control words (font, color, paragraph, etc.) are ignored
                    }
                } else if (ch === '\n' || ch === '\r') {
                    pos++;
                } else {
                    currentText += ch;
                    pos++;
                }
            }

            flushRun();

            // Trim whitespace from first and last runs
            if (runs.length > 0) {
                runs[0].text = runs[0].text.replace(/^\s+/, '');
                runs[runs.length - 1].text = runs[runs.length - 1].text.replace(/\s+$/, '');
            }
            return runs.filter(function(r) { return r.text.length > 0; });
        } catch (e) {
            return [];
        }
    }

    // Extract plain text from RTF (for editing mode).
    extractTextFromRtf(rtfData) {
        return this.parseRtfRuns(rtfData).map(function(r) { return r.text; }).join('');
    }

    mapAlignment(value) {
        var v = String(value || '');
        if (v.includes('LEFT') || v === '0') return 'left';
        if (v.includes('RIGHT') || v === '1') return 'right';
        if (v.includes('CENTER') || v === '2') return 'center';
        if (v.includes('JUSTIFIED') || v === '3') return 'justify';
        if (v.includes('NATURAL') || v === '4') return 'left';
        return 'left';
    }

    getTextProps(element) {
        const el = element.element;
        if (!el || !el.text) return null;

        const textEl = el.text;
        const attrs = textEl.attributes || {};
        const font = attrs.font || {};
        const paragraphStyle = attrs.paragraphStyle || {};
        const textFill = attrs.textSolidFill || {};

        // Derive default bold/italic from the protobuf font attributes
        const defaultBold = font.bold || (font.name && /bold/i.test(font.name)) || false;
        const defaultItalic = font.italic || (font.name && /italic/i.test(font.name)) || false;

        const runs = this.parseRtfRuns(textEl.rtfData, { bold: defaultBold, italic: defaultItalic });
        const content = runs.map(function(r) { return r.text; }).join('');

        const color = this.rgbaFromProtoColor(textFill) || '#ffffff';
        // Prefer font.family (CSS family name) over font.name (specific face name)
        const familyName = (font.family || '').trim();
        const faceFallback = (font.name || '').replace(/\s*(bold|italic|oblique|heavy|light|medium|regular|thin|black|semibold|demibold|ultra|condensed)/gi, '').trim();
        const fontFamily = (familyName || faceFallback) ? ((familyName || faceFallback) + ', sans-serif') : 'sans-serif';
        const fontSize = font.size || 48;

        const textAlign = this.mapAlignment(paragraphStyle.alignment);

        let justifyContent = 'center';
        const vAlign = String(textEl.verticalAlignment || '');
        if (vAlign.includes('TOP') || vAlign === '0') justifyContent = 'flex-start';
        else if (vAlign.includes('MIDDLE') || vAlign === '1') justifyContent = 'center';
        else if (vAlign.includes('BOTTOM') || vAlign === '2') justifyContent = 'flex-end';

        return { content, runs, color, fontFamily, fontSize, textAlign, justifyContent };
    }

    renderElement(element, index) {
        const el = element.element;
        if (!el) return null;
        if (el.hidden) return null;

        const isSelected = index === this.props.selectedElementIndex;
        const isEditing = index === this.state.editingElementIndex;
        const style = this.getElementStyle(element);
        const isText = el.text !== undefined;

        if (el.fill && el.fill.color) {
            style.backgroundColor = this.rgbaFromProtoColor(el.fill.color);
        }

        if (el.stroke && el.stroke.width) {
            style.border = el.stroke.width + 'px solid ' + (this.rgbaFromProtoColor(el.stroke.color) || '#ffffff');
        }

        const textProps = isText ? this.getTextProps(element) : null;

        const textStyle = textProps ? {
            color: textProps.color,
            fontFamily: textProps.fontFamily,
            fontSize: textProps.fontSize + 'px',
            lineHeight: 1.2,
            textAlign: textProps.textAlign,
            wordBreak: 'break-word',
        } : null;

        return (
            <div
                key={index}
                style={style}
                className={"cursor-pointer " + (isSelected ? "ring-2 ring-blue-400 ring-offset-1" : "") + (isEditing ? " ring-2 ring-yellow-400 ring-offset-1" : "")}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isEditing) return;
                    if (isSelected && isText) {
                        this.enterEditMode(index, element);
                    } else {
                        this.props.onSelectElement(index);
                    }
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (isText && !isEditing) {
                        this.props.onSelectElement(index);
                        this.enterEditMode(index, element);
                    }
                }}
            >
                {textProps && !isEditing && (
                    <div
                        className="w-full h-full flex flex-col overflow-hidden"
                        style={{
                            justifyContent: textProps.justifyContent,
                            padding: '8px',
                        }}
                    >
                        <div style={textStyle}>
                            {textProps.runs.map(function(run, ri) {
                                return React.createElement('span', {
                                    key: ri,
                                    style: {
                                        fontWeight: run.bold ? 'bold' : 'normal',
                                        fontStyle: run.italic ? 'italic' : 'normal',
                                        textDecoration: run.underline ? 'underline' : 'none',
                                    }
                                }, run.text);
                            })}
                        </div>
                    </div>
                )}
                {textProps && isEditing && (
                    <div
                        className="w-full h-full flex flex-col overflow-hidden"
                        style={{
                            justifyContent: textProps.justifyContent,
                            padding: '8px',
                        }}
                    >
                        <div
                            ref={this.editableRef}
                            contentEditable
                            suppressContentEditableWarning
                            style={{
                                ...textStyle,
                                outline: 'none',
                                cursor: 'text',
                                minHeight: '1em',
                            }}
                            onBlur={() => this.exitEditMode(true)}
                            onKeyDown={this.handleEditKeyDown}
                        >
                            {textProps.content}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    getMediaAction(cue) {
        if (!cue) return null;
        return (cue.actions || []).find(action => action.media != null) || null;
    }

    getMediaFilename(mediaAction) {
        if (!mediaAction || !mediaAction.media || !mediaAction.media.element) return null;
        const url = mediaAction.media.element.url;
        if (!url) return null;
        const path = (url.local && url.local.path) || url.absolute_string || '';
        return path.split('/').pop() || null;
    }

    isVideoFile(filename) {
        if (!filename) return false;
        return /\.(mp4|mov|m4v|avi|wmv|mkv|webm)$/i.test(filename);
    }

    getMediaUrl(filename) {
        const docId = this.props.documentId;
        if (!docId || !filename) return null;
        return '/slides/editor/' + docId + '/media/' + encodeURIComponent(filename);
    }

    getSlideBackgroundColor(cue) {
        if (!cue) return null;
        const actions = cue.actions || [];
        for (const action of actions) {
            const baseSlide = action.slide && action.slide.presentation && action.slide.presentation.baseSlide;
            if (baseSlide && baseSlide.background_color) {
                return this.rgbaFromProtoColor(baseSlide.background_color);
            }
        }
        return null;
    }

    render() {
        const { cue, remoteCursors, collaborators } = this.props;
        const elements = this.getAllElements(cue);
        const bgColor = this.getSlideBackgroundColor(cue);

        const scaledWidth = 1920 * this.state.scale;
        const scaledHeight = 1080 * this.state.scale;

        return (
            <div ref={this.containerRef} className="flex justify-center">
                <div style={{ width: scaledWidth, height: scaledHeight, position: 'relative', overflow: 'hidden' }}>
                    <div
                        style={{
                            width: 1920,
                            height: 1080,
                            transform: 'scale(' + this.state.scale + ')',
                            transformOrigin: 'top left',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            backgroundColor: bgColor || undefined,
                        }}
                        className="bg-gray-900 rounded-lg shadow-lg"
                        onClick={() => {
                            this.exitEditMode(true);
                            this.props.onSelectElement(null);
                        }}
                    >
                    {(() => {
                        const mediaAction = this.getMediaAction(cue);
                        if (mediaAction) {
                            const mediaFilename = this.getMediaFilename(mediaAction);
                            const mediaUrl = this.getMediaUrl(mediaFilename);
                            const isVideo = this.isVideoFile(mediaFilename);
                            if (mediaUrl) {
                                return (
                                    <div style={{
                                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                        zIndex: 0, backgroundColor: '#000',
                                    }}>
                                        {isVideo ? (
                                            <video
                                                src={mediaUrl}
                                                muted
                                                preload="metadata"
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <img
                                                src={mediaUrl}
                                                alt={mediaFilename}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        )}
                                    </div>
                                );
                            }
                        }
                        return null;
                    })()}
                    {elements.map((element, index) => this.renderElement(element, index))}

                    {/* Remote cursors */}
                    {remoteCursors && Object.entries(remoteCursors).map(([userId, cursor]) => {
                        const collab = (collaborators || []).find(c => c.id === userId);
                        if (!collab) return null;
                        return (
                            <div
                                key={userId}
                                className="absolute pointer-events-none"
                                style={{ top: 10, right: 10 + Object.keys(remoteCursors).indexOf(userId) * 80 }}
                            >
                                <span className="text-xs bg-yellow-400 text-yellow-900 px-1 rounded-sm">
                                    {collab.name} - Slide {(cursor.slide_index || 0) + 1}
                                </span>
                            </div>
                        );
                    })}
                    </div>
                </div>
            </div>
        );
    }
}

export default SlideCanvas;
